/**
 * Hook de cálculo de precios del flujo de reserva.
 *
 * Reemplaza al `handleTotalAmount` + `useEffect` reactivo de
 * `PaymentDetailsStep` que era la fuente probable del bug 10 -> 9.
 *
 * Convención:
 *   - Todo se calcula en CENTAVOS internamente.
 *   - Los expones como números decimales solo en la frontera del UI
 *     (`amount`, `discountAmount`, `finalAmount`).
 *   - El único punto donde `advanceAmount` y `totalAmount` cambian es
 *     `handleTotalAmount`. Eso elimina la carrera entre input y effect.
 */

import { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { toast } from 'react-toastify';
import {
  toCents,
  fromCents,
  formatAmount,
  sumCents,
  equalsAmount,
  subtractCents,
  calculateDiscountCents,
  convertToForeignCurrency
} from '../utils/money';
import { STAY_TYPE_NIGHT } from '../constants';

export const useReservationPricing = ({ selectedRoomData, roomData, stayTypes }) => {
  const [amount, setAmount] = useState(0); // monto base en decimal (para UI)
  const [promoCode, setPromoCode] = useState(null);
  const [splitPayments, setSplitPayments] = useState([]);
  const [showSplitPayments, setShowSplitPayments] = useState(false);

  /**
   * Precio nocturno (en centavos) de una habitación.
   *
   * Fuente de verdad: el arreglo `pricing[]` que escribe la Matriz de Tarifas.
   * El campo legacy `room.amount` solo se usa como último recurso porque puede
   * quedar desactualizado (la sincronización de la matriz NO lo toca y las
   * habitaciones nuevas lo guardan en 0). Leer el legacy era la causa del bug
   * "precio configurado distinto al cobrado en la reserva".
   */
  const resolveNightlyPriceCents = (room) => {
    if (!room) return 0;
    const nightlyType = stayTypes?.find((t) => t.isNightly);
    const entry = nightlyType ? room.pricing?.find((p) => String(p.stayTypeId) === String(nightlyType._id)) : null;
    if (entry && entry.amount != null) return toCents(entry.amount);
    return toCents(room.amount || 0);
  };

  // Cuando cambia la habitación seleccionada (o cargan los stay types)
  // actualizamos el precio base desde la matriz, no desde el legacy.
  useEffect(() => {
    if (selectedRoomData) {
      setAmount(fromCents(resolveNightlyPriceCents(selectedRoomData)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoomData, stayTypes]);

  // Descuento derivado del promo code, en centavos para precisión.
  const discountCents = useMemo(
    () => calculateDiscountCents(toCents(amount), promoCode),
    [amount, promoCode]
  );

  // Monto final tras descuento, también derivado (no almacenado).
  const finalAmountCents = useMemo(
    () => subtractCents(toCents(amount), discountCents),
    [amount, discountCents]
  );

  const discountAmount = fromCents(discountCents);
  const finalAmount = fromCents(finalAmountCents);

  /**
   * Recalcula amount cuando cambian roomNo, stayType, numberOfNights o
   * checkInDate. Replica la lógica original pero centralizada.
   */
  const calculateAmount = (values, setFieldValue) => {
    // Fallback a selectedRoomData: en el primer render tras cargar los datos,
    // Formik aún no re-inicializó values.roomNo (llega '') y el find falla,
    // dejando la fecha de salida sin calcular. Es seguro: cuando hay
    // selectedRoomData el campo roomNo es readonly, así que nunca divergen.
    const selectedRoom = roomData?.find((room) => room.roomNo === values.roomNo) || selectedRoomData;
    if (!selectedRoom) return;

    const calculateCheckoutDate = (checkInMoment, nights) => {
      const hotelDataObj = JSON.parse(localStorage.getItem('hotelData') || '{}');

      let baseCheckoutTime = moment('14:00', 'HH:mm');
      if (hotelDataObj.checkOutTime) {
        const timeObj = moment(hotelDataObj.checkOutTime, ['h:mm A']);
        if (timeObj.isValid()) baseCheckoutTime = timeObj;
      }

      const checkInTimeOnly = moment(checkInMoment.format('HH:mm'), 'HH:mm');
      let checkOutMoment;
      if (checkInTimeOnly.isBefore(baseCheckoutTime)) {
        checkOutMoment = checkInMoment.clone().add(nights - 1, 'days');
      } else {
        checkOutMoment = checkInMoment.clone().add(nights, 'days');
      }

      checkOutMoment.set({
        hour: baseCheckoutTime.get('hour'),
        minute: baseCheckoutTime.get('minute'),
        second: 0
      });

      return checkOutMoment.format('YYYY-MM-DDTHH:mm');
    };

    const hotelInputToMoment = (dateString) => {
      const tz = JSON.parse(localStorage.getItem('hotelData') || '{}')?.timezone || 'America/Caracas';
      return moment.tz(dateString, tz);
    };

    const selectedStayType = stayTypes.find((type) => type._id === values.stayType);

    // Path legacy: stayType === 'night' o no hay configuración dinámica.
    if (!selectedStayType) {
      if (values.stayType === STAY_TYPE_NIGHT || !values.stayType) {
        if (!values.checkInDate) return;
        const checkInMoment = hotelInputToMoment(values.checkInDate);
        if (!checkInMoment.isValid()) return;

        const nights = parseInt(values.numberOfNights, 10) || 1;
        const updatedCheckOutDate = calculateCheckoutDate(checkInMoment, nights);
        if (updatedCheckOutDate !== values.checkOutDate && setFieldValue) {
          setFieldValue('checkOutDate', updatedCheckOutDate);
        }

        // Precio desde la matriz (pricing[]), con el legacy como último recurso.
        const priceCents = resolveNightlyPriceCents(selectedRoom);
        setAmount(fromCents(nights * priceCents));
      }
      return;
    }

    // Path dinámico: el hotel definió tipos de estancia con sus precios.
    const pricingEntry = selectedRoom.pricing?.find((p) => p.stayTypeId === selectedStayType._id);
    const priceCents = toCents(pricingEntry ? pricingEntry.amount : 0);

    if (selectedStayType.isNightly) {
      if (!values.checkInDate) {
        setAmount(fromCents(priceCents));
        return;
      }
      const checkInMoment = hotelInputToMoment(values.checkInDate);
      if (!checkInMoment.isValid()) {
        setAmount(fromCents(priceCents));
        return;
      }

      const nights = parseInt(values.numberOfNights, 10) || 1;
      const updatedCheckOutDate = calculateCheckoutDate(checkInMoment, nights);
      if (updatedCheckOutDate !== values.checkOutDate && setFieldValue) {
        setFieldValue('checkOutDate', updatedCheckOutDate);
      }

      setAmount(fromCents(nights * priceCents));
      return;
    }

    setAmount(fromCents(priceCents));

    if (selectedStayType.hours && values.checkInDate) {
      const checkInMoment = hotelInputToMoment(values.checkInDate);
      if (checkInMoment.isValid()) {
        const checkOutMoment = checkInMoment.clone().add(selectedStayType.hours, 'hours');
        setFieldValue('checkOutDate', checkOutMoment.format('YYYY-MM-DDTHH:mm'));
      }
    }
  };

  /**
   * Único punto de escritura para advanceAmount y totalAmount.
   *
   * Reglas:
   *  - Si el usuario borra el campo, advance vuelve a "" y total al monto
   *    final completo.
   *  - El anticipo nunca puede superar el monto final.
   *  - total = final - advance, siempre en centavos para evitar el bug 10->9.
   *
   * Importante: preservamos el string crudo del usuario en `advanceAmount`
   * (sin re-formatear a "X.00"). Re-formatear en cada tecla rompía la
   * entrada de decimales: tipear "10.25" era imposible porque cada
   * pulsación normalizaba a "10.00". El formato definitivo se aplica al
   * enviar (vía `buildReservationFormData`), no mientras se escribe.
   */
  const handleTotalAmount = (advanceValue, setFieldValue) => {
    if (advanceValue === '' || advanceValue === null || advanceValue === undefined) {
      setFieldValue('advanceAmount', '');
      setFieldValue('totalAmount', formatAmount(finalAmountCents));
      return;
    }

    const advanceCents = toCents(advanceValue);
    if (advanceCents < 0) return;

    if (advanceCents <= finalAmountCents) {
      // Conservamos el valor tal cual lo tipeó el usuario para que pueda
      // seguir editándolo (añadir decimales, borrar dígitos, etc.).
      setFieldValue('advanceAmount', advanceValue);
      setFieldValue('totalAmount', formatAmount(subtractCents(finalAmountCents, advanceCents)));
    } else {
      // Solo aquí re-formateamos: estamos clamping a un valor que el
      // usuario no tipeó (el máximo permitido).
      setFieldValue('advanceAmount', formatAmount(finalAmountCents));
      setFieldValue('totalAmount', '0.00');
      toast.warning('El anticipo no puede ser mayor al total a pagar');
    }
  };

  /**
   * Recalcula totalAmount cuando finalAmount cambia (ej. al aplicar un
   * promo code). Llamarlo manualmente desde los componentes para evitar
   * effects en cadena que escriban Formik.
   */
  const syncTotalAmount = (advanceAmount, setFieldValue) => {
    const advanceCents = toCents(advanceAmount);
    if (advanceCents > finalAmountCents) {
      setFieldValue('advanceAmount', formatAmount(finalAmountCents));
      setFieldValue('totalAmount', '0.00');
      return;
    }
    setFieldValue('totalAmount', formatAmount(subtractCents(finalAmountCents, advanceCents)));
  };

  /**
   * Recalcula el monto en moneda extranjera para el modo simple cuando
   * cambia la moneda o el final amount.
   */
  const syncForeignCurrencyAmount = (paymentCurrency, exchangeRates, setFieldValue) => {
    if (!paymentCurrency || paymentCurrency === 'USD') return;
    const rate = exchangeRates.find((r) => r.abbreviation === paymentCurrency)?.rate || 1;
    setFieldValue('amountInForeignCurrency', convertToForeignCurrency(finalAmountCents, rate));
  };

  // -- Split payments -----------------------------------------------------

  const addSplitPayment = () => {
    const newPayment = {
      id: Date.now() + Math.floor(Math.random() * 1000),
      paymentMethod: 'cash',
      amount: '',
      currency: 'USD',
      exchangeRateUsed: 1,
      amountInForeignCurrency: 0,
      batchNumber: '',
      transactionNumber: ''
    };
    setSplitPayments((prev) => [...prev, newPayment]);
  };

  const removeSplitPayment = (paymentId) => {
    setSplitPayments((prev) => prev.filter((p) => p.id !== paymentId));
  };

  const updateSplitPayment = (paymentId, field, value, exchangeRates) => {
    setSplitPayments((prev) =>
      prev.map((payment) => {
        if (payment.id !== paymentId) return payment;

        const updated = { ...payment, [field]: value };

        if (field === 'currency') {
          if (value === 'USD') {
            updated.exchangeRateUsed = 1;
            updated.amountInForeignCurrency = 0;
          } else {
            const rate = exchangeRates.find((r) => r.abbreviation === value);
            if (rate) {
              updated.exchangeRateUsed = rate.rate;
              if (updated.amount) {
                updated.amountInForeignCurrency = convertToForeignCurrency(toCents(updated.amount), rate.rate);
              }
            }
          }
        }

        if (field === 'amount' && updated.currency !== 'USD') {
          updated.amountInForeignCurrency = convertToForeignCurrency(toCents(value), updated.exchangeRateUsed);
        }

        return updated;
      })
    );
  };

  const getTotalPaidCents = () => sumCents(splitPayments.map((p) => p.amount));
  const getTotalPaid = () => fromCents(getTotalPaidCents());

  const areSplitPaymentsValid = (advanceAmount) => {
    if (!showSplitPayments) return true;
    if (splitPayments.length === 0) return false;

    const advanceCents = toCents(advanceAmount);
    if (advanceCents <= 0) return false;

    const allComplete = splitPayments.every((p) => {
      const hasMethod = p.paymentMethod && p.paymentMethod !== '';
      const hasAmount = toCents(p.amount) > 0;
      return hasMethod && hasAmount;
    });

    return allComplete && equalsAmount(getTotalPaidCents(), advanceCents);
  };

  const resetSplitPayments = () => {
    setSplitPayments([]);
    setShowSplitPayments(false);
  };

  return {
    // estado expuesto al UI
    amount,
    discountAmount,
    finalAmount,
    promoCode,
    setPromoCode,
    splitPayments,
    setSplitPayments,
    showSplitPayments,
    setShowSplitPayments,

    // valores en centavos para callers que necesitan precisión
    finalAmountCents,
    discountCents,

    // acciones
    calculateAmount,
    handleTotalAmount,
    syncTotalAmount,
    syncForeignCurrencyAmount,
    addSplitPayment,
    removeSplitPayment,
    updateSplitPayment,
    getTotalPaid,
    areSplitPaymentsValid,
    resetSplitPayments
  };
};
